import type { ReactNode } from 'react';
import { Bot, LineChart, MessageSquareHeart, Star } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * A dark, pill-shaped "floating stat badge" — a near-complete SVG ring around a big number, plus a short legend —
 * meant to overlap the bottom-left corner of a ScreenshotMockup, exactly the callout treatment used throughout this
 * skill's reference: a real metric surfaced as its own floating element rather than buried inside the screenshot.
 */
function FloatingStatBadge({
    value,
    valueLabel,
    ringColor,
    legend,
}: {
    value: string;
    valueLabel: string;
    ringColor: string;
    legend: Array<{ text: string; pct: string; color: string }>;
}) {
    const size = 72;
    const stroke = 6;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;

    return (
        <div className="absolute -bottom-6 left-5 flex items-center gap-3 rounded-full bg-[#0f172a] py-2 pl-2 pr-5 shadow-2xl ring-1 ring-white/10">
            <div className="relative shrink-0" style={{ width: size, height: size }}>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                    <circle cx={size / 2} cy={size / 2} r={radius} fill="none" strokeWidth={stroke} className="stroke-white/10" />
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        strokeWidth={stroke}
                        strokeLinecap="round"
                        style={{
                            stroke: ringColor,
                            strokeDasharray: circumference,
                            strokeDashoffset: circumference * 0.15,
                            transform: 'rotate(-90deg)',
                            transformOrigin: `${size / 2}px ${size / 2}px`,
                        }}
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-base font-extrabold leading-none text-white">{value}</span>
                    <span className="mt-1 text-center text-[7px] font-medium leading-none text-white/50">{valueLabel}</span>
                </div>
            </div>
            <div className="space-y-1.5">
                {legend.map((l) => (
                    <div key={l.text} className="flex items-center gap-1.5 whitespace-nowrap text-[10.5px] text-white/80">
                        <span className="size-1.5 shrink-0 rounded-full" style={{ background: l.color }} />
                        {l.text} <span className="text-white/45">{l.pct}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/**
 * A mini "app screenshot" frame — dark icon rail + light content panel — behind a soft blurred color blob, with a
 * FloatingStatBadge overlapping its bottom-left corner. One shared shell for all three product mockups so they read
 * as a consistent product family, matching that reference image's screenshot-plus-floating-metric composition.
 */
function ScreenshotMockup({
    accent,
    sidebarActiveIndex,
    badge,
    children,
}: {
    accent: string;
    sidebarActiveIndex: number;
    badge: ReactNode;
    children: ReactNode;
}) {
    return (
        <div className="relative w-full max-w-[320px] pb-9">
            <div
                className="absolute inset-x-6 top-8 h-36 rounded-full opacity-25 blur-2xl"
                style={{ background: `radial-gradient(circle, ${accent}, transparent 70%)` }}
            />
            <div className="relative overflow-hidden rounded-xl border bg-card shadow-2xl">
                <div className="grid grid-cols-[36px_1fr]">
                    <div className="space-y-2.5 bg-[#0f172a] p-2.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <div
                                key={i}
                                className="h-1.5 rounded-full"
                                style={{ background: i === sidebarActiveIndex ? accent : 'rgba(255,255,255,0.15)' }}
                            />
                        ))}
                    </div>
                    <div className="p-3">{children}</div>
                </div>
            </div>
            {badge}
        </div>
    );
}

/** Product-mockup screenshots (one per flagship product) — each echoing that product's own real in-app color (navy/emerald/violet, matching GradientCardHeader's exact hex values). */
export function AiAssistantBannerVisual() {
    return (
        <ScreenshotMockup
            accent="#2563eb"
            sidebarActiveIndex={1}
            badge={
                <FloatingStatBadge
                    value="97"
                    valueLabel="conversations"
                    ringColor="#2563eb"
                    legend={[
                        { text: 'Resolved', pct: '82 · 85%', color: '#2563eb' },
                        { text: 'Escalated', pct: '15 · 15%', color: '#94a3b8' },
                    ]}
                />
            }
        >
            <div className="mb-2 h-2 w-2/3 rounded bg-muted" />
            <div className="space-y-1.5">
                <div className="max-w-[80%] rounded-lg rounded-tl-sm bg-muted px-2.5 py-1.5 text-[10px]">How can I help you today?</div>
                <div className="ml-auto max-w-[70%] rounded-lg rounded-tr-sm bg-[#2563eb] px-2.5 py-1.5 text-[10px] text-white">
                    Book a tour
                </div>
                <div className="rounded-lg border border-dashed border-emerald-400 px-2.5 py-1.5 text-center text-[9.5px] font-medium text-emerald-600">
                    ✓ Appointment booked — Thu 2:00 PM
                </div>
            </div>
        </ScreenshotMockup>
    );
}

export function PricingAssistantBannerVisual() {
    const bars = [55, 70, 45, 85, 65, 60, 78];

    return (
        <ScreenshotMockup
            accent="#10b981"
            sidebarActiveIndex={2}
            badge={
                <FloatingStatBadge
                    value="+8%"
                    valueLabel="vs. market"
                    ringColor="#10b981"
                    legend={[
                        { text: 'Outstanding', pct: '6 · 75%', color: '#10b981' },
                        { text: 'Watch', pct: '2 · 25%', color: '#f59e0b' },
                    ]}
                />
            }
        >
            <div className="mb-2 flex items-baseline justify-between">
                <div className="h-2 w-1/2 rounded bg-muted" />
                <span className="text-[9px] font-semibold text-emerald-600">Rate / Sq Ft</span>
            </div>
            <div className="flex h-20 items-end gap-1.5">
                {bars.map((h, i) => (
                    <div key={i} className={`flex-1 rounded-t-sm ${i === 3 ? 'bg-[#10b981]' : 'bg-muted'}`} style={{ height: `${h}%` }} />
                ))}
            </div>
        </ScreenshotMockup>
    );
}

export function SentimentAnalysisBannerVisual() {
    return (
        <ScreenshotMockup
            accent="#8b5cf6"
            sidebarActiveIndex={3}
            badge={
                <FloatingStatBadge
                    value="87"
                    valueLabel="reputation"
                    ringColor="#8b5cf6"
                    legend={[
                        { text: 'Positive', pct: '68%', color: '#10b981' },
                        { text: 'Negative', pct: '8%', color: '#ef4444' },
                    ]}
                />
            }
        >
            <div className="mb-2 flex items-center justify-between">
                <div className="h-2 w-1/2 rounded bg-muted" />
                <div className="flex items-center gap-0.5 text-amber-400">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className="size-2.5 fill-current" />
                    ))}
                </div>
            </div>
            <div className="flex h-2 overflow-hidden rounded-full">
                <div className="bg-emerald-500" style={{ width: '68%' }} />
                <div className="bg-slate-300" style={{ width: '24%' }} />
                <div className="bg-red-400" style={{ width: '8%' }} />
            </div>
            <p className="mt-1.5 text-[9px] text-muted-foreground">4.8 average across 1,200+ reviews</p>
        </ScreenshotMockup>
    );
}

export interface ProductInfo {
    key: string;
    gradient: string;
    icon: LucideIcon;
    eyebrow: string;
    title: string;
    description: string;
    longDescription: string;
    bullets: string[];
    visual: ReactNode;
}

/** AIPA's three flagship products — single source of truth reused by the homepage's ProductsSection and the contact page's ProductSlider. */
export const PRODUCTS: ProductInfo[] = [
    {
        key: 'ai-assistant',
        gradient: 'from-[#1e3a5f] to-[#2563eb]',
        icon: Bot,
        eyebrow: 'Product 01',
        title: 'AI Assistant',
        description:
            'A guided, conversational assistant that greets every visitor, answers questions instantly, and books appointments — on your website, 24/7.',
        longDescription:
            'Most website chatbots either follow a rigid script or hallucinate answers your team never approved. AI Assistant does neither — it walks each visitor through a real branching decision tree your team configures once, so every answer is guided and on-brand, while free-text questions are answered from your own knowledge base, never invented. The result is a visitor who gets a useful answer immediately, and a team that gets a qualified, context-rich lead instead of a blank contact form.',
        bullets: [
            'Branching guided-question flows, no code required',
            'Handles web chat, voice, and WhatsApp from one setup',
            'Every conversation becomes a scored, ready-to-close lead',
            'Answers grounded in your own knowledge base — never fabricated',
            'Live on your site in minutes with a single embeddable script tag',
        ],
        visual: <AiAssistantBannerVisual />,
    },
    {
        key: 'pricing-assistant',
        gradient: 'from-[#065f46] to-[#10b981]',
        icon: LineChart,
        eyebrow: 'Product 02',
        title: 'Pricing Assistant',
        description: 'Real-time competitor pricing intelligence, so you always know exactly where your rates stand in the market.',
        longDescription:
            'Pricing decisions made on stale, manually-collected competitor data are already out of date by the time they’re made. Pricing Assistant tracks the specific competitors you choose, keeps their rates current, and shows you exactly how your own rate — by unit type, by community — compares. Every recommendation is grounded in your own tracked data, never a generic industry benchmark, so the number you act on is the one that actually applies to your market.',
        bullets: [
            'Live rate comparisons against your tracked competitors',
            'Community-by-community market positioning at a glance',
            'AI-backed recommendations for where to move your pricing',
            'Rate-per-square-foot and unit-type breakdowns, not just one blended number',
            'A full comparison matrix, not a single "market average" guess',
        ],
        visual: <PricingAssistantBannerVisual />,
    },
    {
        key: 'sentiment-analysis',
        gradient: 'from-[#4c1d95] to-[#8b5cf6]',
        icon: MessageSquareHeart,
        eyebrow: 'Product 03',
        title: 'Sentiment Analysis',
        description:
            'AI-powered review analysis across every source, so you understand exactly what customers think — and never miss a signal.',
        longDescription:
            'Reviews pile up faster than any team can read them, and the ones that matter most — a sudden run of negative feedback, a recurring complaint — are easy to miss buried in the noise. Sentiment Analysis reads every review the moment it comes in, scores it, extracts the keywords driving it, and rolls it all up into one reputation score you can track over time — with your own tracked competitors shown right alongside it, so you know not just how you’re doing, but how you’re doing relative to who you’re actually competing with.',
        bullets: [
            'Every review, every source, analyzed automatically',
            'Reputation scoring and sentiment trends over time',
            'Instant alerts — plus email & SMS — the moment a review turns negative',
            'Keyword extraction that surfaces exactly what customers keep mentioning',
            'Side-by-side comparison against the competitors you track',
        ],
        visual: <SentimentAnalysisBannerVisual />,
    },
];
